import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import axiosServices from "../utils/axios";

import { fetchCompanyList } from "./company";
import { fetchEmailsList, fetchFolders, fetchFavoriteFolders, saveFavoriteFolders, fetchScheduledSends } from "./outlookService";

import type {
  Person,
  Company,
  Vessel,
  DocumentType,
  VesselJob,
  VesselNotification,
  NewPersonPayload,
  NewCompanyPayload,
  PickList,
  EmailListItem,
  Documents,
  MailboxKey,
} from "../utils/types/email";
import type { FavoriteFolder } from "./outlookService";

const DEFAULT_OUTLOOK_FOLDER_QUERY_KEY = "__default_inbox__";

interface UpdateVoyagesPayload {
  voyages: string[]; // Array of job_ids
  mailbox: MailboxKey;
  conversation_id?: string;
  message_id?: string;
  subject?: string;
  from_email?: string;
  to_recipients?: string[];
  received_datetime?: string;
  parent_folder_id?: string;
}

export const updateEmailVoyages = async (
  email: EmailListItem,
  jobToAdd: VesselJob,
  mailbox: MailboxKey
): Promise<any> => {
  const currentVoyageIds = (email.voyageJobs || [])
    .map((job) => job.job_id)
    .filter((id): id is string => !!id);

  console.log("Existing Voyage IDs:", currentVoyageIds);

  const newVoyageId = jobToAdd.job_id;
  console.log("New Job ID to Add:", newVoyageId);
  console.log(email)

  if (!newVoyageId) {
    throw new Error("The selected job is missing a required 'job_id'.");
  }

  const finalVoyageIds = Array.from(
    new Set([...currentVoyageIds, newVoyageId])
  );

  console.log("Final list of Voyage IDs to send:", finalVoyageIds);

  const payload: UpdateVoyagesPayload = {
    voyages: finalVoyageIds,
    mailbox,
  };

  // If the email is not yet processed, we must send the full metadata.
  if (!email.processed) {
    payload.conversation_id = email.conversationId || undefined;
    payload.message_id = email.id;
    payload.subject = email.subject;
    payload.from_email = email.from.emailAddress.address;
    payload.to_recipients = [
      ...(email.to || []),
      ...(email.ccRecipients || []),
    ].map((r) => r.emailAddress.address);
    payload.received_datetime = email.receivedDateTime;
  }

  if (email.parentFolderId) {
    payload.parent_folder_id = email.parentFolderId;
  }

  const response = await axiosServices.put(
    `/emailmessages/${email.id}/voyages`,
    payload
  );
  return response.data;
};

export const fetchInternalData = async <T>(
  url: string,
  params?: any
): Promise<T> => {
  const response = await axiosServices.get<{ data: T }>(url, { params });
  return response.data.data;
};

export const usePeople = () =>
  useQuery({
    queryKey: ["people"],
    queryFn: () => fetchInternalData<Person[]>("/people"),
    staleTime: Infinity,
  });
export const useCompanies = () =>
  useQuery({
    queryKey: ["companies"],
    queryFn: () => fetchInternalData<Company[]>("/company"),
    staleTime: Infinity,
  });
export const useVessels = () =>
  useQuery({
    queryKey: ["vessels"],
    queryFn: () => fetchInternalData<Vessel[]>("/vessel"),
    staleTime: Infinity,
  });
export const useDocumentTypes = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["documentTypes"],
    queryFn: () => fetchInternalData<DocumentType[]>("/documenttype"),
    staleTime: Infinity,
    enabled,
  });

export const useDocuments = (documentId?: string, enabled: boolean = true) =>
  useQuery({
    queryKey: ["documents", documentId],
    queryFn: () => fetchInternalData<Documents[]>("/documents"),
    staleTime: Infinity,
    enabled,
  });

export const useVesselDetails = (
  vesselId: string | null,
  options: { include?: string } = {}
) =>
  useQuery({
    queryKey: ["vessel", vesselId, options.include],
    queryFn: () => {
      if (!vesselId) return null;
      return fetchInternalData<Vessel>(`/vessel/${vesselId}`, options);
    },
    enabled: !!vesselId,
    staleTime: 1000 * 60 * 5,
  });
export const useOutlookEmails = (params: {
  folderId?: string;
  filter?: string;
  top?: number;
  enabled?: boolean;
  orderby?: string;
  mailbox: MailboxKey;
}) => {
  const { enabled, folderId, mailbox, ...requestParams } = params;
  const normalizedFolderId = folderId || undefined;

  return useInfiniteQuery({
    queryKey: [
      "outlookEmails",
      mailbox,
      normalizedFolderId ?? DEFAULT_OUTLOOK_FOLDER_QUERY_KEY,
      requestParams.filter,
      requestParams.orderby,
    ],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      return fetchEmailsList({
        ...requestParams,
        folderId: normalizedFolderId,
        top: params.top || 100,
        mailbox,
        cursor: pageParam,
      });
    },
    initialPageParam: null as string | null,
    // Use the backend-supplied cursor rather than inferring position from result
    // count — the backend may return fewer items than pageSize when filtering,
    // so a short page does NOT mean there are no more messages.
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled !== false,
  });
};

export const useFolders = (mailbox: MailboxKey, enabled?: boolean) => {
  return useQuery({
    queryKey: ["outlookFolders", mailbox],
    queryFn: () => fetchFolders(mailbox),
    staleTime: Infinity, // Cache forever - only reload on app restart
    retry: false,
    throwOnError: false,
    enabled: enabled !== false,
  });
};

export const useScheduledSends = (mailbox?: MailboxKey, enabled?: boolean) => {
  return useQuery({
    queryKey: ["outlookScheduled", mailbox ?? "all"],
    queryFn: () => fetchScheduledSends(mailbox),
    enabled: enabled !== false,
  });
};

export const useVesselsWithNotifications = (vesselIds: string[]) =>
  useQuery({
    queryKey: ["vesselsWithNotifications", vesselIds],
    queryFn: async () => {
      if (vesselIds.length === 0) return null;
      const idsString = vesselIds.join(",");
      const response = await axiosServices.get<{
        data: Vessel[];
        notifications: { vessels: VesselNotification[] };
      }>(`/vessel/${idsString}`, { params: { include: "notifications" } });
      return response.data;
    },
    enabled: vesselIds.length > 0,
  });

export const useJobs = (
  enabled: boolean = true,
  version: "active" | "historical" = "active",
  searchQuery: string = ""
) => {
  const isHistory = version === "historical";
  const query = searchQuery.trim();
  const hasQuery = isHistory && query.length > 0;

  return useInfiniteQuery({
    queryKey: ["jobs", version, query],
    queryFn: async ({ pageParam = 0 }) => {
      const params: Record<string, unknown> = {
        format: "berthdata",
        version,
        sort: JSON.stringify([
          isHistory
            ? { property: "billing_code", direction: "DESC" }
            : { property: "assigned_agent", direction: "ASC" },
        ]),
      };

      if (hasQuery) {
        // Server-side search — paginated so user can scroll through all matches
        params.q = query;
        params.start = pageParam * 50;
        params.limit = 50;
        params.page = pageParam + 1;
      } else if (isHistory) {
        // No query — load most recent 200 records upfront
        params.start = 0;
        params.limit = 200;
        params.page = 1;
      } else {
        params.start = pageParam * 50;
        params.limit = 50;
        params.page = pageParam + 1;
      }

      const response = await axiosServices.get<{
        data: VesselJob[];
        total: number;
      }>("/jobs", { params });

      return response.data.data || [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // History with no query = single fetch, no more pages
      if (isHistory && !hasQuery) return undefined;
      if (!lastPage || lastPage.length < 50) return undefined;
      return allPages.length;
    },
    enabled,
  });
};

export const addPerson = async (payload: NewPersonPayload): Promise<Person> => {
  const response = await axiosServices.post<{ data: Person }>(
    "/people",
    payload
  );
  return response.data.data;
};

export const addCompany = async (
  payload: NewCompanyPayload
): Promise<Company> => {
  const response = await axiosServices.post<{ data: Company }>(
    "/company",
    payload
  );
  return response.data.data;
};

export const updatePerson = async (
  personId: string,
  payload: Partial<NewPersonPayload>
): Promise<Person> => {
  const response = await axiosServices.put<{ data: Person }>(
    `/people/${personId}`,
    payload
  );
  return response.data.data;
};

export const updateCompany = async (
  companyId: string,
  payload: Partial<NewCompanyPayload>
): Promise<Company> => {
  const response = await axiosServices.put<{ data: Company }>(
    `/company/${companyId}`,
    payload
  );
  return response.data.data;
};
export const usePickList = () =>
  useQuery({
    queryKey: ["picklist"],
    queryFn: () => fetchInternalData<PickList[]>("/pickList"),
    staleTime: Infinity,
  });

export const associateVesselToEmail = async (
  emailId: string,
  vesselId: string
): Promise<any> => {
  // This is a placeholder for the real API call.
  // The backend would store this association.
  console.log(`ASSOCIATING Vessel ${vesselId} to Email ${emailId}`);
  return { success: true };
};

export const addTagToEmail = async (
  emailId: string,
  tagId: string
): Promise<any> => {
  console.log(`TAGGING Email ${emailId} with Tag ${tagId}`);
  return { success: true };
};

export const disassociateVesselFromEmail = async (
  email: EmailListItem,
  vesselIdToRemove: string,
  mailbox: MailboxKey
): Promise<any> => {
  const finalVoyageIds = email.voyageJobs
    .filter((job) => job.vessel_id !== vesselIdToRemove)
    .map((job) => job.job_id);

  const payload = { voyages: finalVoyageIds, mailbox };
  const response = await axiosServices.put(
    `/emailmessages/${email.id}/voyages`,
    payload
  );
  return response.data;
};

export const useCompaniesForJob = (
  portCallJobId: string | null,
  showCompletedBerths?: boolean
) =>
  useQuery({
    queryKey: ["portCallCompanyList", portCallJobId, showCompletedBerths],
    queryFn: () => fetchCompanyList(portCallJobId!, showCompletedBerths),
    enabled: !!portCallJobId,
    staleTime: 0,
  });

export const fetchPortCallNotification = async (params: {
  agentOnly: boolean;
  pcid: string;
  hqOnly: boolean;
  showCompletedBerth: boolean;
  companyId?: string;
  companyCategory?: string;
  terminalBerthIds?: string[];
  isAllArrangements?: boolean;
  version?: "v1" | "v2" | "final";
}) => {
  const {
    agentOnly,
    pcid,
    hqOnly,
    showCompletedBerth,
    companyId,
    companyCategory,
    isAllArrangements,
    version = "v1",
  } = params;
  const endpoint = version === "v2" ? "portcallnotification-v2" : "portcallnotification";
  let url = `/email/${endpoint}?agentOnly=${agentOnly}&pcid=${pcid}&hqOnly=${hqOnly}`;
  if (showCompletedBerth) {
    url += `&showCompletedBerth=${showCompletedBerth}`;
  }
  if (companyId) {
    url += `&companyId=${companyId}`;
    if (companyCategory) {
      url += `&companyCategory=${companyCategory}`;
    }
  }
  if (isAllArrangements) {
    url += `&isAllArrangements=${isAllArrangements}`;
  }
  if (params.terminalBerthIds && params.terminalBerthIds.length > 0) {
    const berthIdsParam = params.terminalBerthIds.join(",");
    url += `&terminalBerthIds=${berthIdsParam}`;
  }
  const response = await axiosServices.get(`${url}`);
  return response;
};

export const usePortCallNotification = (params: {
  agentOnly: boolean;
  pcid: string;
  hqOnly: boolean;
  showCompletedBerth: boolean;
  companyId?: string;
  companyCategory?: string;
  terminalBerthIds?: string[];
  isAllArrangements?: boolean;
  version?: "v1" | "v2" | "final";
}) =>
  useQuery({
    queryKey: [
      "portCallNotification",
      params.pcid,
      params.agentOnly,
      params.hqOnly,
      params.showCompletedBerth,
      params.companyId,
      params.companyCategory,
      params.terminalBerthIds,
      params.isAllArrangements,
      params.version,
    ],
    queryFn: () => fetchPortCallNotification(params),
    enabled: !!params.pcid && params.version !== "final",
    staleTime: 0,
  });

export const useFavoriteFolders = (mailbox: MailboxKey) =>
  useQuery({
    queryKey: ["favoriteFolders", mailbox],
    queryFn: () => fetchFavoriteFolders(mailbox),
    staleTime: 1000 * 60 * 5,
    retry: false,
    throwOnError: false,
  });

export const useSaveFavoriteFolders = (mailbox: MailboxKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (favorites: Pick<FavoriteFolder, "folderId" | "displayName">[]) =>
      saveFavoriteFolders(mailbox, favorites),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favoriteFolders", mailbox] });
    },
  });
};

export interface PrearrivalFormOption {
  fileName: string;
  formId: string;
  mimeType: string;
  name: string;
  size: number | null;
}

export interface PrearrivalDraftRecipient {
  email: string;
  name?: string | null;
  source?: string;
}

export interface PrearrivalDraftAttachment {
  bucket?: string;
  contentType?: string;
  documentId: string;
  fileName?: string;
  key?: string;
  mimeType?: string;
  name: string;
  size?: number | null;
}

export interface PrearrivalDraftResponse {
  attachments: PrearrivalDraftAttachment[];
  html: string;
  recipients: {
    cc: PrearrivalDraftRecipient[];
    to: PrearrivalDraftRecipient[];
  };
  shipRecipientAvailable: boolean;
  subject: string;
  warnings?: string[];
}

export const fetchJobPrearrivalForms = async (jobId: string) => {
  const response = await axiosServices.get<{
    data: PrearrivalFormOption[];
    total: number;
  }>(`/jobs/${encodeURIComponent(jobId)}/prearrival-form`);
  return response.data;
};

export const useJobPrearrivalForms = (
  jobId: string | null,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: ["jobPrearrivalForms", jobId],
    queryFn: () => fetchJobPrearrivalForms(jobId!),
    enabled: enabled && !!jobId,
    staleTime: 5 * 60 * 1000,
  });

export const fetchPrearrivalDraft = async (
  jobId: string,
  formId: string
): Promise<PrearrivalDraftResponse> => {
  const response = await axiosServices.get<PrearrivalDraftResponse>(
    `/email/prearrival`,
    {
      params: {
        formId,
        jobId,
      },
    }
  );
  return response.data;
};

